import { Autocomplete, TextField, Typography } from "@mui/material";
import {
  outlinedPickerStyles,
  pickerArrowStyles,
  StyledFormControl,
  StyledFormInput,
  StyledFormLabelWrapper,
  StyledValidationText,
} from "Components/Shared/FormStyles";
import { ExpandArrowIcon } from "Components/Shared/Icons";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { useCountryList } from "Hooks/Options/useCountryList";
import type { CountryCode } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import { useId } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";

type Props<FormType extends FieldValues, TransformedFieldValues = FormType> = {
  name: FieldPath<FormType>;
  control: Control<FormType, unknown, TransformedFieldValues>;
  label?: string;
  /** Name for screen readers when no visible label is rendered (e.g. a checkbox above already names the field). */
  accessibilityLabel?: string;
  isDisabled?: boolean;
  /** Greyed out, not hidden — the first nationality while picking the second one (null = nothing picked yet).
   *  Stays visible when it is already the field's value, so the "must differ" error points at something. */
  excludedCode?: CountryCode | null;
  // Fires after the field value updates: the picked code, or null when the user clears the field.
  onChange?: (value: CountryCode | null) => void;
};

type CountryOption = CodeListItem<CountryCode>;

// A plain wrapper carries the picker look (shared with the Select in Components/Shared/Inputs/Dropdown),
// so the generic Autocomplete itself is not wrapped in styled() and needs no type cast.
const StyledAutocompleteWrapper = styled.div`
  width: 100%;

  .MuiOutlinedInput-root {
    ${outlinedPickerStyles}
    border-radius: 0;
  }

  .MuiAutocomplete-popupIndicator {
    ${pickerArrowStyles}
  }
`;

const getOptionLabel = ({ name }: CountryOption) => name;
const isOptionEqualToValue = (option: CountryOption, value: CountryOption) =>
  option.code === value.code;

/**
 * Country picker over the whole ISO 3166-1 list (~250 entries): a searchable autocomplete, with the
 * home-market countries pinned first and the rest alphabetical (see useCountryList). The field value
 * is the ISO alpha-2 code, null when cleared.
 */
export const CountryDropdown = <T extends object, V extends object>({
  control,
  name,
  label,
  accessibilityLabel,
  isDisabled = false,
  excludedCode,
  onChange: onValueChange,
}: Props<T, V>) => {
  const { t } = useResource();
  const inputId = useId();
  const { codeListCountries } = useCountryList();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, value, onBlur, ref },
        fieldState: { error },
      }) => {
        const selected =
          codeListCountries.find(({ code }) => code === value) ?? null;

        return (
          <StyledFormControl>
            {!!label && (
              <StyledFormLabelWrapper>
                <StyledInputLabel hasError={!!error} htmlFor={inputId}>
                  <Typography>{label}</Typography>
                </StyledInputLabel>
              </StyledFormLabelWrapper>
            )}
            <StyledFormInput>
              <StyledAutocompleteWrapper>
                <Autocomplete
                  id={inputId}
                  fullWidth
                  autoHighlight
                  options={codeListCountries}
                  getOptionDisabled={({ code }) => code === excludedCode}
                  value={selected}
                  onChange={(_event, option) => {
                    const code = option?.code ?? null;
                    onChange(code);
                    onValueChange?.(code);
                  }}
                  onBlur={onBlur}
                  disabled={isDisabled}
                  getOptionLabel={getOptionLabel}
                  isOptionEqualToValue={isOptionEqualToValue}
                  noOptionsText={t(
                    Resources.Components.Shared.Dropdown.NoOptions,
                  )}
                  openText={t(Resources.Common.Open)}
                  closeText={t(Resources.Common.Close)}
                  clearText={t(Resources.Common.Clear)}
                  popupIcon={<ExpandArrowIcon />}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      error={!!error}
                      inputRef={ref}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          "aria-label": accessibilityLabel ?? label,
                        },
                      }}
                    />
                  )}
                />
              </StyledAutocompleteWrapper>
            </StyledFormInput>
            <StyledValidationText>
              {!!error && error.message}
            </StyledValidationText>
          </StyledFormControl>
        );
      }}
    />
  );
};
